'use client';
import { useUserEventLogger } from '@repo/tracking-service';
import { BackButtonTopNavigation, BaseTopNavigation } from '@repo/ui';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';
import { ReactNode } from 'react';

import { HeaderRightButtons, LogoHeaderLeftButtons } from '@/app/_components/layout-content/HeaderItems';
import { useHeaderSelector } from '@/lib/hooks/use-platform';
import { navigationBarTitleAtom } from '@/lib/stores/platform';
import { LayoutContainerClassNames, LayoutHeaderProps } from '@/lib/types/layout';

export const LogoHeader = ({ isUseInformationUse = false, isUseIntroduce = true, isUseAppMenu = true, isUseShare = false }: LayoutHeaderProps) => {
  return (
    <header>
      <BaseTopNavigation
        className={'bg-white min-w-80'}
        leftAction={<LogoHeaderLeftButtons />}
        rightAction={
          <HeaderRightButtons isUseInformationUse={isUseInformationUse} isUseShare={isUseShare} isUseAppMenu={isUseAppMenu} isUseIntroduce={isUseIntroduce} />
        }
      />
    </header>
  );
};

export const BackButtonHeader = ({
  isUseInformationUse = false,
  isUseShare = false,
  isUseAppMenu = true,
  isUseIntroduce = false,
  isUseNewCHat = false
}: LayoutHeaderProps) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const navigationBarTitle = useAtomValue(navigationBarTitleAtom);

  const backButtonAction = () => {
    sendClickEvent('topnav_back');
    router.back();
  };

  return (
    <header>
      <BackButtonTopNavigation
        className={'bg-white min-w-80'}
        title={<div className={'ellipsis_webkit_box'}>{navigationBarTitle}</div>}
        onBackClick={backButtonAction}
        rightAction={
          <HeaderRightButtons
            isUseInformationUse={isUseInformationUse}
            isUseShare={isUseShare}
            isUseAppMenu={isUseAppMenu}
            isUseIntroduce={isUseIntroduce}
            isUseNewCHat={isUseNewCHat}
          />
        }
      />
    </header>
  );
};

export const ConversionHeader = ({ isUseInformationUse = false, isUseShare = false, isUseAppMenu = true, isUseIntroduce = false }: LayoutHeaderProps) => {
  const headerType = useHeaderSelector();
  return (
    <>
      {headerType === 'logo' && (
        <LogoHeader isUseInformationUse={isUseInformationUse} isUseShare={isUseShare} isUseIntroduce={isUseIntroduce} isUseAppMenu={isUseAppMenu} />
      )}
      {headerType === 'back' && (
        <BackButtonHeader isUseInformationUse={isUseInformationUse} isUseShare={isUseShare} isUseAppMenu={isUseAppMenu} isUseIntroduce={isUseIntroduce} />
      )}
    </>
  );
};

const DefaultContainer = ({ children, className = '' }: { children: ReactNode; className?: string }) => {
  return <div className={`container container-large container-padding-medium flex flex-col justify-center ${className}`}>{children}</div>;
};

export const MainContainer = ({ children, classNames }: { children: ReactNode; classNames?: LayoutContainerClassNames }) => {
  return (
    <main className={`flex flex-col items-center flex-1 ${classNames?.main ?? ''}`}>
      <DefaultContainer className={`${classNames?.container ?? ''}`}>{children}</DefaultContainer>
    </main>
  );
};

export const DefaultFooter = () => (
  <footer className="flex justify-center py-spacious-large">
    <DefaultContainer className={'text-neutral-foreground-low'}>
      <div>
        <div className={'body-small-semibold'}>지엔터프라이즈</div>
        <div className={'flex flex-col gap-tiny-large pt-normal-medium body-small-regular'}>
          <div>대표 이성봉</div>
          <div>사업자번호 236-88-00397</div>
          <div>통신판매업신고 제2016-서울강남-04379호</div>
          <div>서울특별시 강남구 테헤란로 507, 9층</div>
          <div>제휴 문의 contact@zent.kr</div>
        </div>
        <div className={'pt-spacious-small caption-medium-regular text-neutral-foreground-lowest'}>Copyright © Z Enterprise, Inc. All rights reserved.</div>
      </div>
    </DefaultContainer>
  </footer>
);
